type TrackName = 'hero' | 'story' | 'success';

const TRACK_MAP: Record<TrackName, string> = {
  hero: '/audio/hero-bgm.mp3',
  story: '/audio/story-bgm.mp3',
  success: '/audio/success-bgm.mp3',
};

class SoundManager {
  private static instance: SoundManager;
  private audio: HTMLAudioElement | null = null;
  private currentTrack: TrackName | null = null;
  private _muted = false;
  private preloaded = new Map<string, HTMLAudioElement>();

  private constructor() {}

  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  preload(tracks: TrackName[] = ['hero', 'story', 'success']) {
    tracks.forEach((track) => {
      const src = TRACK_MAP[track];
      if (!this.preloaded.has(src)) {
        try {
          const audio = new Audio(src);
          audio.preload = 'auto';
          this.preloaded.set(src, audio);
        } catch {
          console.log(`[SoundManager] Failed to preload: ${src}`);
        }
      }
    });
  }

  playMusic(track: TrackName) {
    if (this.currentTrack === track && this.audio && !this.audio.paused) return;

    this.stopMusic();
    const src = TRACK_MAP[track];

    try {
      const preloaded = this.preloaded.get(src);
      this.audio = preloaded
        ? (preloaded.cloneNode() as HTMLAudioElement)
        : new Audio(src);
      this.audio.loop = true;
      this.audio.muted = this._muted;

      this.audio.play().catch(() => {
        console.log(`[SoundManager] Cannot play track: ${track}`);
      });

      this.currentTrack = track;
    } catch {
      console.log(`[SoundManager] Error playing track: ${track}`);
    }
  }

  pauseMusic() {
    if (this.audio) {
      this.audio.pause();
    }
  }

  stopMusic() {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = null;
    }
  }

  toggleMute(): boolean {
    this._muted = !this._muted;
    if (this.audio) {
      this.audio.muted = this._muted;
    }
    return this._muted;
  }

  get muted(): boolean {
    return this._muted;
  }

  get playingTrack(): TrackName | null {
    return this.currentTrack;
  }
}

export const soundManager = SoundManager.getInstance();
export default soundManager;
